import type { NotificationDto, useNotificationApi } from "@/api/notifications";

type NotificationStreamApi = Pick<ReturnType<typeof useNotificationApi>, "getList" | "getChanges" | "getUnreadCount">;

/** Owned by the enclosing embedded session so token renewal can temporarily unmount the stream. */
export type NotificationStreamCheckpoint = { agentId?: string; cursor?: number };

export function prepareNotificationStreamCheckpoint(checkpoint: NotificationStreamCheckpoint, agentId: string): void {
  if (checkpoint.agentId === agentId) return;
  delete checkpoint.cursor;
  checkpoint.agentId = agentId;
}

/** Minimal SignalR surface, also usable with an in-memory connection in tests. */
export type NotificationStreamConnection = {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  on: (methodName: string, callback: (signal: { maxCursor: number }) => void) => void;
  off: (methodName: string, callback: (signal: { maxCursor: number }) => void) => void;
  onreconnected: (callback: () => void) => void;
  onclose: (callback: () => void) => void;
};

export type NotificationStreamOptions = {
  api: NotificationStreamApi;
  connection: NotificationStreamConnection;
  checkpoint?: NotificationStreamCheckpoint;
  onNotificationReceived: (notification: NotificationDto) => void;
  onUnreadCountChanged: (count: number) => void;
  retryDelayMs?: number;
  unreadRefreshIntervalMs?: number;
};

/**
 * A session-scoped notification stream. Its initial cursor skips existing history;
 * the first catch-up runs after connecting so notifications in that gap are retained.
 * No notification UI, browser storage or Web Push registration is involved.
 */
export function subscribeToNotifications({
  api,
  connection,
  checkpoint,
  onNotificationReceived,
  onUnreadCountChanged,
  retryDelayMs = 5_000,
  unreadRefreshIntervalMs = 60_000,
}: NotificationStreamOptions): () => void {
  let stopped = false;
  let cursor = checkpoint?.cursor;
  let connected = false;
  let starting = false;
  let syncing = false;
  let changesRequested = false;
  let unreadRefreshing = false;
  let unreadRequested = false;
  let lastUnreadCount: number | undefined;
  let startRetry: ReturnType<typeof setTimeout> | undefined;
  let changesRetry: ReturnType<typeof setTimeout> | undefined;
  let unreadRetry: ReturnType<typeof setTimeout> | undefined;
  let unreadTimer: ReturnType<typeof setTimeout> | undefined;

  async function refreshUnread() {
    if (stopped || unreadRefreshing || unreadRetry !== undefined) return;
    unreadRefreshing = true;
    try {
      while (unreadRequested && !stopped) {
        unreadRequested = false;
        const result = await api.getUnreadCount();
        if (stopped) return;
        if (result.count !== lastUnreadCount) {
          lastUnreadCount = result.count;
          onUnreadCountChanged(result.count);
        }
      }
    } catch {
      if (!stopped) {
        unreadRequested = true;
        unreadRetry = setTimeout(() => {
          unreadRetry = undefined;
          void refreshUnread();
        }, retryDelayMs);
      }
    } finally {
      unreadRefreshing = false;
    }
  }

  function requestUnread() {
    if (stopped) return;
    unreadRequested = true;
    void refreshUnread();
  }

  async function syncChanges() {
    if (stopped || syncing || cursor === undefined || changesRetry !== undefined) return;
    syncing = true;
    try {
      while (changesRequested && !stopped) {
        changesRequested = false;
        let hasMore = false;
        do {
          const afterCursor = cursor;
          const result = await api.getChanges({ afterCursor, limit: 100 });
          if (stopped) return;
          for (const notification of result.items) {
            if (notification.cursor > cursor) {
              cursor = notification.cursor;
              if (checkpoint) checkpoint.cursor = cursor;
              onNotificationReceived(notification);
              if (stopped) return;
            }
          }
          cursor = Math.max(cursor, result.nextCursor);
          if (checkpoint) checkpoint.cursor = cursor;
          hasMore = result.hasMore;
          if (hasMore && cursor <= afterCursor) {
            throw new Error("Notification changes did not advance the cursor.");
          }
        } while (hasMore && !stopped);
        requestUnread();
      }
    } catch {
      if (!stopped) {
        changesRequested = true;
        changesRetry = setTimeout(() => {
          changesRetry = undefined;
          void syncChanges();
        }, retryDelayMs);
      }
    } finally {
      syncing = false;
    }
  }

  function requestChanges() {
    if (stopped) return;
    changesRequested = true;
    void syncChanges();
  }

  function scheduleStart() {
    if (stopped || startRetry !== undefined) return;
    startRetry = setTimeout(() => {
      startRetry = undefined;
      void start();
    }, retryDelayMs);
  }

  async function start() {
    if (stopped || connected || starting) return;
    starting = true;
    try {
      if (cursor === undefined) {
        const latest = await api.getList({ limit: 1 });
        if (stopped) return;
        cursor = latest.items[0]?.cursor ?? 0;
        if (checkpoint) checkpoint.cursor = cursor;
      }
      await connection.start();
      if (stopped) {
        void connection.stop().catch(() => {});
        return;
      }
      connected = true;
      requestChanges();
      requestUnread();
    } catch {
      scheduleStart();
    } finally {
      starting = false;
    }
  }

  function onAvailable(signal: { maxCursor: number }) {
    if (stopped) return;
    if (cursor !== undefined && signal.maxCursor > cursor) requestChanges();
    requestUnread();
  }

  connection.on("notification.available", onAvailable);
  connection.onreconnected(() => {
    if (stopped) return;
    connected = true;
    requestChanges();
    requestUnread();
  });
  connection.onclose(() => {
    connected = false;
    scheduleStart();
  });

  function scheduleUnreadRefresh() {
    unreadTimer = setTimeout(() => {
      if (stopped) return;
      requestUnread();
      scheduleUnreadRefresh();
    }, unreadRefreshIntervalMs);
  }

  requestUnread();
  scheduleUnreadRefresh();
  void start();

  return () => {
    if (stopped) return;
    stopped = true;
    for (const timer of [startRetry, changesRetry, unreadRetry, unreadTimer]) {
      if (timer !== undefined) clearTimeout(timer);
    }
    connection.off("notification.available", onAvailable);
    void connection.stop().catch(() => {});
  };
}
